import Papa from 'papaparse'
import type { ParsedData, Curve, Event, SurveyMeasurement } from '../../../interfaces/toolData'
import type { WaveFileType } from '../types'
import { cleanTimeSeriesData } from '../../dataQuality'

const FILE_TYPE: WaveFileType = 'PHX_VELOCITY_DEFAULT'

/**
 * Phoenix Velocity (EVO) CSV Parser
 * - Handles repeated header blocks (select last header before valid data)
 * - Discards year 2000 lines
 * - Extracts sparse time series and interspersed survey measurements
 */
export class PhoenixVelocityParser {
  static canParse(content: string): boolean {
    if (!content || !content.trim()) return false
    const lines = content.split('\n')
    const hasBanner = lines[0]?.includes('EVOLog-') || lines[0]?.toUpperCase().includes('MAIN,EVOL')
    const hasLogInfo = content.includes('Log Information,Firmware Version:')
    const hasCommand = content.includes('Command,') && content.includes('Code:38-')
    const hasDateHeader = content.includes('Date,Sync Counter,Flow Switch Status')
    return !!(hasBanner && hasLogInfo && hasCommand && hasDateHeader)
  }

  static parse(fileContent: string, filename: string = ''): ParsedData {
    if (!fileContent || !fileContent.trim()) throw new Error('File is empty')

    const lines = fileContent.split('\n')

    // Locate all header blocks
    type HeaderBlock = { logInfoIndex: number; commandIndex: number; columnsIndex: number }
    const headers: HeaderBlock[] = []
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('Log Information')) {
        // Expect next two lines: Command,... then Date,...
        const commandIndex = lines.slice(i + 1).findIndex(l => l.startsWith('Command'))
        const columnsIndex = lines.slice(i + 1).findIndex(l => l.startsWith('Date,'))
        if (commandIndex >= 0 && columnsIndex >= 0 && columnsIndex > commandIndex) {
          headers.push({ logInfoIndex: i, commandIndex: i + 1 + commandIndex, columnsIndex: i + 1 + columnsIndex })
        }
      }
    }
    if (headers.length === 0) throw new Error('Phoenix/EVO header not found')

    // Find first valid data row (timestamp >= 2010 and not 2000); choose the header that immediately precedes it
    const isValidTs = (s: string): boolean => {
      const d = PhoenixVelocityParser.parseChicagoLocal(s)
      if (!d) return false
      const y = d.getFullYear()
      return y >= 2010 // discard 2000-era timestamps
    }

    let chosenHeader: HeaderBlock | undefined
    // Scan from bottom for robustness, capturing first valid data and nearest previous header
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (/^[A-Za-z]{3}\s+\d{1,2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}/.test(line)) {
        // Begins with date like "Jul 19 2025 22:05:20"
        const dateStr = line.split(',')[0]
        if (isValidTs(dateStr)) {
          // Find nearest header preceding this line
          const candidates = headers.filter(h => h.columnsIndex < i)
          if (candidates.length > 0) {
            chosenHeader = candidates[candidates.length - 1] // last preceding header
            break
          }
        }
      }
    }

    if (!chosenHeader) {
      // Fallback to last header if none precede valid data
      chosenHeader = headers[headers.length - 1]
    }

    // Parse CSV from columns row using PapaParse
    const csv = lines.slice(chosenHeader.columnsIndex).join('\n')
    const result = Papa.parse(csv, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false
    })
    const rows = (result.data as Record<string, string>[]) || []

    // Extract metadata from the chosen Log Information line
    const logInfoLine = lines[chosenHeader.logInfoIndex]
    const metadata = PhoenixVelocityParser.extractMetadata(logInfoLine)
    metadata.filename = filename
    metadata.vendor = 'Phoenix (EVO)'

    // Build curves and surveys
    const curves: Curve[] = []
    const columnInfo: Record<string, { unit: string; description?: string }> = {}
    const surveys: SurveyMeasurement[] = []

    // Predefine unit mappings for key columns
    const unitMap: Record<string, string> = {
      'Gamma CPS': 'cps',
      'Inclination Survey': 'degrees',
      'Azimuth Survey': 'degrees',
      'GTotal Survey': 'degrees',
      'MTotal Survey': 'gauss',
      'Mag Dip Angle Survey': 'degrees',
      'MP Bus Voltage': 'V',
      'Battery Voltage-Uphole': 'V',
      'Battery Voltage-Downhole': 'V',
      'Battery Current-Uphole': 'A',
      'Battery Current-Downhole': 'A',
      'RPM 1 Sec Avg': 'RPM',
      'Bore Pressure': 'psi',
      'Shock Axial RMS': 'G', 'Shock Axial Level': 'G', 'Shock Axial Max': 'G',
      'Shock Lateral RMS': 'G', 'Shock Lateral Level': 'G', 'Shock Lateral Max': 'G',
      'Continuous Inclination': 'degrees',
      'Continuous Azimuth': 'degrees'
    }

    // Series collection by column name
    const series: Record<string, { times: Date[]; values: number[]; unit: string }> = {}
    const addPoint = (name: string, t: Date, v: number) => {
      const unit = unitMap[name] || ''
      if (!series[name]) series[name] = { times: [], values: [], unit }
      series[name].times.push(t)
      series[name].values.push(v)
      if (!columnInfo[name]) columnInfo[name] = { unit }
    }

    // Event buckets
    const eventBuckets: Record<string, Date[]> = {}
    const addEvent = (name: string, t: Date) => {
      if (!eventBuckets[name]) eventBuckets[name] = []
      eventBuckets[name].push(t)
    }

    // Iterate rows, filter year 2000, parse timestamp and values
    for (const row of rows) {
      const tsRaw = (row['Date'] || '').toString().trim()
      if (!tsRaw) continue
      const ts = PhoenixVelocityParser.parseChicagoLocal(tsRaw)
      if (!ts || isNaN(ts.getTime()) || ts.getFullYear() < 2010) continue

      // Extract surveys when present
      const sInc = parseFloat((row['Inclination Survey'] || '').toString())
      const sAz = parseFloat((row['Azimuth Survey'] || '').toString())
      const sGtot = parseFloat((row['GTotal Survey'] || '').toString())
      const sMtot = parseFloat((row['MTotal Survey'] || '').toString())
      const sDip = parseFloat((row['Mag Dip Angle Survey'] || '').toString())
      const sAx = parseFloat((row['D&I Ax Survey'] || '').toString())
      const sAy = parseFloat((row['D&I Ay Survey'] || '').toString())
      const sAzA = parseFloat((row['D&I Az Survey'] || '').toString())
      const sMx = parseFloat((row['D&I Mx Survey'] || '').toString())
      const sMy = parseFloat((row['D&I My Survey'] || '').toString())
      const sMz = parseFloat((row['D&I Mz Survey'] || '').toString())

      const hasSurvey = !isNaN(sInc) && !isNaN(sAz)
      if (hasSurvey) {
        const survey: SurveyMeasurement = {
          time: ts,
          inclination: sInc,
          azimuth: sAz,
          totalGrav: isFinite(sGtot) ? sGtot : undefined,
          mx: isFinite(sMx) ? sMx : undefined,
          my: isFinite(sMy) ? sMy : undefined,
          mz: isFinite(sMz) ? sMz : undefined,
          magF: isFinite(sMtot) ? sMtot : undefined,
          dipAngle: isFinite(sDip) ? sDip : undefined,
          ax: isFinite(sAx) ? sAx : undefined,
          ay: isFinite(sAy) ? sAy : undefined,
          az: isFinite(sAzA) ? sAzA : undefined
        }
        surveys.push(survey)
      }

      // Numeric columns of interest
      const numericColumns = Object.keys(unitMap)
      for (const col of numericColumns) {
        const raw = row[col]
        if (raw === undefined || raw === null || raw === '') continue
        const val = parseFloat(raw as string)
        if (!isNaN(val) && isFinite(val)) {
          addPoint(col, ts, val)
        }
      }

      // Flow Switch Status: interpret OFF:0 / ON:1 and emit as both event and time series
      const fssRaw = (row['Flow Switch Status'] || '').toString().trim()
      if (fssRaw) {
        let fssVal: number | null = null
        if (/^ON:?\s*1/i.test(fssRaw)) { fssVal = 1; addEvent('Flow Switch ON', ts) }
        else if (/^OFF:?\s*0/i.test(fssRaw)) { fssVal = 0; addEvent('Flow Switch OFF', ts) }
        else {
          const v = parseFloat(fssRaw)
          if (!isNaN(v)) fssVal = v
        }
        if (fssVal !== null) {
          addPoint('Flow Switch Status', ts, fssVal)
          if (!columnInfo['Flow Switch Status']) columnInfo['Flow Switch Status'] = { unit: '' }
        }
      }

      // Text markers as events
      const rowStr = Object.values(row).join(' ')
      const expiredMatch = rowStr.match(/Expired\s+Survey:(\d+)/i)
      if (expiredMatch) addEvent('Expired Survey', ts)
      const onMatch = rowStr.match(/\bON:(\d+)/i)
      if (onMatch) addEvent('ON', ts)
      const headerMatch = rowStr.match(/Header:(\d+)/i)
      if (headerMatch) addEvent('Header', ts)
    }

    // Convert series to curves
    for (const [name, s] of Object.entries(series)) {
      if (s.times.length === 0) continue
      const cleaned = cleanTimeSeriesData({ name, xValues: s.times, yValues: s.values, yUnit: s.unit }).cleanedCurve
      if (cleaned.xValues.length > 0) curves.push(cleaned)
    }

    // Build events list
    const eventList: Event[] = Object.entries(eventBuckets).map(([name, times]) => ({ name, xValues: times }))

    // Data preview count
    const totalCount = curves.reduce((acc, c) => Math.max(acc, c.xValues.length), 0)

    return {
      toolType: 'Phoenix',
      fileType: FILE_TYPE,
      curves,
      surveys: surveys.length > 0 ? surveys : undefined,
      events: eventList.length > 0 ? eventList : undefined,
      columnInfo,
      metadata,
      dataPreview: { totalCount },
      errors: result.errors.map(e => e.message)
    }
  }

  private static extractMetadata(logInfoLine: string): Record<string, string> {
    // Format: Log Information,Firmware Version: V...,Config ID:...,Config File Name:...,Config Last Modified Date Time: ...
    const meta: Record<string, string> = {}
    const parts = logInfoLine.split(',')
    for (const p of parts) {
      const [k, ...rest] = p.split(':')
      if (!k || rest.length === 0) continue
      const key = k.trim()
      const value = rest.join(':').trim()
      if (key.startsWith('Firmware Version')) meta.firmwareVersion = value
      else if (key.startsWith('Config ID')) meta.configId = value
      else if (key.startsWith('Config File Name')) meta.configFileName = value
      else if (key.startsWith('Config Last Modified Date Time')) meta.configLastModified = value
      else if (key.startsWith('Interval Time')) meta.intervalTime = value
      else if (key.startsWith('Data Change Tolerance')) meta.dataChangeTolerance = value
    }
    return meta
  }

  // Parse timestamps like "Jul 19 2025 22:05:20" as America/Chicago (CST/CDT) local time,
  // and return a Date in UTC that represents that wall-clock time. This ensures consistent display
  // regardless of the viewer's machine timezone while preserving CST/CDT intent.
  private static parseChicagoLocal(s: string): Date | null {
    const m = s.match(/^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/)
    if (!m) return null
    const monMap: Record<string, number> = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 }
    const month = monMap[m[1]]
    if (month === undefined) return null
    const day = parseInt(m[2], 10)
    const year = parseInt(m[3], 10)
    const hour = parseInt(m[4], 10)
    const minute = parseInt(m[5], 10)
    const second = parseInt(m[6], 10)

    const offset = PhoenixVelocityParser.getChicagoOffsetHours(year, month, day)
    // UTC time = local (Chicago) + offset
    const ms = Date.UTC(year, month, day, hour + offset, minute, second)
    return new Date(ms)
  }

  // Return offset hours to convert Chicago local -> UTC (5 in DST, 6 otherwise)
  private static getChicagoOffsetHours(year: number, month: number, day: number): number {
    // US DST rules: starts second Sunday in March, ends first Sunday in November
    const isDST = PhoenixVelocityParser.isUsDstInChicago(year, month, day)
    return isDST ? 5 : 6
  }

  private static isUsDstInChicago(year: number, month: number, day: number): boolean {
    // Compute second Sunday in March
    const secondSundayInMarch = PhoenixVelocityParser.nthDowOfMonth(year, 2, 0, 2) // Mar=2, Sunday=0
    // Compute first Sunday in November
    const firstSundayInNov = PhoenixVelocityParser.nthDowOfMonth(year, 10, 0, 1) // Nov=10

    const current = Date.UTC(year, month, day)
    const dstStart = Date.UTC(year, 2, secondSundayInMarch, 2, 0, 0) // 2:00 AM local
    const dstEnd = Date.UTC(year, 10, firstSundayInNov, 2, 0, 0)
    return current >= dstStart && current < dstEnd
  }

  private static nthDowOfMonth(year: number, month: number, dow: number, n: number): number {
    // Find the date (day of month) of the nth occurrence of a day-of-week in a month
    const firstOfMonth = new Date(Date.UTC(year, month, 1))
    const firstDow = firstOfMonth.getUTCDay() // 0=Sun
    let day = 1 + ((7 + dow - firstDow) % 7) // first occurrence of dow
    day += (n - 1) * 7
    return day
  }
}
